import { useCallback, useEffect, useRef, useState } from "react";
import { useMap, AdvancedMarker, InfoWindow } from "@vis.gl/react-google-maps";
import { useIsComponentMounted } from "../../../hooks/useIsComponentMounted";
import { useNitroStore } from "../../../store/Store";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import NitroMarker from "./NitroMarker";
import { IMultiLocationRoute } from "../../../types/IMultiLocationRoute";
import "../../../App.css";
import { useIsMobile } from "../../../hooks/useIsMobile";

// TODO: zooming in to map pins is a bit janky, need to investigate better ways to do this (maybe not using fitBounds and calculating zoom/center manually instead?) especially when searching location
const RoutesLayerMultiLocationQuote = () => {
  const isComponentMounted = useIsComponentMounted();
  const map = useMap();

  const [
    selectedLocation,
    labelType,
    multiLocationDestinations,
    product,
    hidePins,
    currentFilters,
  ] = useNitroStore((state) => [
    state.selectedLocation,
    state.labelType,
    state.multiLocationDestinations,
    state.product,
    state.hidePins,
    state.currentFilters,
  ]);

  const hiddenOrigins = useNitroCAStore((state) => state.hiddenOrigins);
  const hiddenOriginsKey = `${Array.from(hiddenOrigins.truck)
    .sort()
    .join(",")}_${Array.from(hiddenOrigins.rail).sort().join(",")}`;

  const isTablet = useIsMobile(1334);

  const routesRef = useRef<any[] | null>(null);
  const [markers, setMarkers] = useState<JSX.Element[]>([]);

  const [tooltipLatLng, setTooltipLatLng] = useState<
    { lat: number; lng: number } | undefined
  >(undefined);
  const [selectedRoutes, setSelectedRoutes] = useState<IMultiLocationRoute[]>(
    [],
  );

  const getLabel = useCallback(
    (route: IMultiLocationRoute, type: "origin" | "destination") => {
      if (!route[type]) return "";
      let label = route[type].city;
      if (labelType === "prices") {
        const values = [];
        if (route[type].name) {
          values.push(route[type].name);
        }
        if (route.maxPrice) {
          values.push(`$${route.maxPrice}`);
        }

        label = values.join(" - ");
      } else if (labelType === "city") {
        label = `${(route[type] as any).city}`;
      } else if (labelType === "location") {
        label = route[type].name;
      } else if (labelType === "facility") {
        label = route[type].owner;
      }
      return label;
    },
    [labelType],
  );

  const load = useCallback(async () => {
    const currentHiddenOrigins = useNitroCAStore.getState().hiddenOrigins;

    let filteredRoutes: IMultiLocationRoute[] = [];

    if (multiLocationDestinations.length > 0) {
      if (selectedLocation) {
        filteredRoutes = multiLocationDestinations.filter(
          (record: IMultiLocationRoute) => {
            const matchesDestination =
              (record.destination?.id === selectedLocation.id ||
                record.destination?.name === selectedLocation.name) &&
              record.freight &&
              record.freight !== 0;

            const mode = record.mode.toLowerCase() as "truck" | "rail";
            const isHidden = currentHiddenOrigins[mode].has(
              `${record.competitor ? "competitor" : "koch"}-${
                record.origin.name
              }`,
            );

            return matchesDestination && !isHidden;
          },
        );
      } else {
        filteredRoutes = multiLocationDestinations;
      }
    } else {
      filteredRoutes = [];
    }

    if (filteredRoutes.length === 0) {
      routesRef.current = [];
      setMarkers([]);
      return;
    }

    routesRef.current = [];
    const newMarkers: JSX.Element[] = [];

    const latLngBounds = new google.maps.LatLngBounds();

    if (!selectedLocation) {
      const uniqueDestinations = new Map<string, IMultiLocationRoute>();
      filteredRoutes.forEach((route) => {
        if (
          route.destination?.id &&
          !uniqueDestinations.has(route.destination.id)
        ) {
          uniqueDestinations.set(route.destination.id, route);
        }
      });

      uniqueDestinations.forEach((route) => {
        if (!route.destination) return;

        const position = {
          lat: route.destination.coordinate?.[0] || 0,
          lng: route.destination.coordinate?.[1] || 0,
        };
        latLngBounds.extend(new google.maps.LatLng(position));

        const label = getLabel(route, "destination");
        const pinType = route.destination.pinType || "city";
        const disabled =
          !route.availableProducts.includes(product) &&
          route.destination.pinType !== "city";

        newMarkers.push(
          <AdvancedMarker
            position={position}
            key={`dest-${route.destination.id}`}
          >
            <NitroMarker type={pinType} label={label} disabled={disabled} />
          </AdvancedMarker>,
        );
      });
    } else {
      const isOriginCompletelyHidden = (originName: string) => {
        return (
          currentHiddenOrigins.truck.has(originName) &&
          currentHiddenOrigins.rail.has(originName)
        );
      };

      const routesWithVisibleOrigins = filteredRoutes.filter(
        (route) =>
          !isOriginCompletelyHidden(
            `${route.competitor ? "competitor" : "koch"}-${route.origin.name}`,
          ),
      );

      const groupedRoutes = routesWithVisibleOrigins.reduce((acc, route) => {
        const key = `${route.origin.id}-${route.destination?.id}`;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(route);
        return acc;
      }, {} as { [key: string]: IMultiLocationRoute[] });

      Object.values(groupedRoutes).forEach((routes, i) => {
        const route = routes[0];
        const occupantTypes: string[] = [];
        routes.forEach((r) =>
          r.occupantTypes.forEach((x) => occupantTypes.push(x)),
        );

        const last = {
          lat: route.origin.coordinate?.[0] || 0,
          lng: route.origin.coordinate?.[1] || 0,
        };
        const first = {
          lat: route.destination?.coordinate?.[0] || 0,
          lng: route.destination?.coordinate?.[1] || 0,
        };

        if (i === 0) {
          latLngBounds.extend(new google.maps.LatLng(first));
          const label = getLabel(route, "destination");

          const destinationPinType =
            route.destination?.pinType || selectedLocation?.pinType || "city";

          newMarkers.push(
            <AdvancedMarker position={first} key="first">
              <NitroMarker type={destinationPinType} label={label} />
            </AdvancedMarker>,
          );
        }

        const line = new google.maps.Polyline({
          path: [first, last],
          strokeWeight: 1,
          strokeColor: "black",
        });

        google.maps.event.addListener(line, "mouseover", ({ latLng }: any) => {
          line.setOptions({ strokeWeight: 4 });
          setTooltipLatLng({ lat: latLng.lat(), lng: latLng.lng() });
          setSelectedRoutes(routes);
        });
        google.maps.event.addListener(line, "mouseout", () => {
          line.setOptions({ strokeWeight: 2 });
          setTooltipLatLng(undefined);
          setSelectedRoutes([]);
        });

        line.setMap(map);
        routesRef.current?.push(line);

        const label = getLabel(route, "origin");

        latLngBounds.extend(new google.maps.LatLng(last));

        let type = "koch";
        if (
          occupantTypes.indexOf("Koch") !== -1 &&
          occupantTypes.indexOf("Competitor") !== -1
        ) {
          type = "kochAndCompetitor";
        } else if (occupantTypes.indexOf("Koch") !== -1) {
          type = "koch";
        } else if (occupantTypes.indexOf("Competitor") !== -1) {
          type = "competitor";
        }

        newMarkers.push(
          <AdvancedMarker
            position={last}
            // eslint-disable-next-line react/no-array-index-key
            key={`origin-${route.origin.id}-${route.origin.name}`}
          >
            <NitroMarker type={type} label={label} />
          </AdvancedMarker>,
        );
      });
    }
    setMarkers(newMarkers);

    if (filteredRoutes.length > 0) {
      setTimeout(() => {
        map?.fitBounds(latLngBounds, {
          top: 150,
          bottom: 150,
          left: isTablet ? 60 : 200,
          right: 150,
        });
        setTimeout(() => {
          if (!selectedLocation) {
            // zoom out further if a party is selected to show more options, zoom in more if a city is selected to focus on the city
            if (currentFilters.party) {
              map?.setZoom(isTablet ? 6 : 8);
            } else {
              map?.setZoom(isTablet ? 6 : 12);
            }
          } else {
            map?.setZoom(6);
          }
        }, 50);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    getLabel,
    multiLocationDestinations,
    selectedLocation,
    labelType,
    isTablet,
    hiddenOriginsKey,
  ]);

  useEffect(() => {
    if (!isComponentMounted) return;
    if (!map) return;

    routesRef.current?.forEach((r) => {
      r.setMap(null);
    });

    const hasMultiLocationData =
      multiLocationDestinations && multiLocationDestinations.length > 0;

    if (!hasMultiLocationData) {
      setMarkers([]);
      routesRef.current = null;
      return;
    }

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    labelType,
    multiLocationDestinations,
    selectedLocation,
    hiddenOriginsKey,
  ]);

  return (
    <>
      {hidePins
        ? markers.filter((m) => !m.props.children.props.disabled)
        : markers}
      {selectedRoutes.length > 0 &&
        tooltipLatLng &&
        (() => {
          const currentHiddenOrigins = useNitroCAStore.getState().hiddenOrigins;

          const visibleRoutes = selectedRoutes.filter((route) => {
            const mode = route.mode.toLowerCase() as "truck" | "rail";
            return !currentHiddenOrigins[mode].has(
              `${route.competitor ? "competitor" : "koch"}-${
                route.origin.name
              }`,
            );
          });

          if (visibleRoutes.length === 0) return null;

          return (
            <InfoWindow position={tooltipLatLng}>
              <div
                style={{
                  backgroundColor: "white",
                  color: "#383838",
                  fontSize: 16,
                }}
              >
                <div style={{ padding: "0px 20px 5px 20px" }}>
                  <b>{`${visibleRoutes[0].origin.city} to ${visibleRoutes[0].destination?.city}`}</b>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    paddingLeft: 25,
                    paddingBottom: 20,
                    fontSize: 14,
                  }}
                >
                  {visibleRoutes.map((route) => (
                    <div
                      key={`${route.origin.id}-${route.mode}-${
                        route.legs[0]?.minCars || 0
                      }`}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <div>
                        {route.mode === "RAIL" &&
                        route.legs[0]?.minCars &&
                        route.legs[0].minCars > 1
                          ? `Mode: ${route.mode} (${route.legs[0].minCars}+ UT)`
                          : `Mode: ${route.mode}`}
                      </div>
                      <div>{`Freight: $${route.freight.toFixed(2)}`}</div>
                    </div>
                  ))}
                </div>
              </div>
            </InfoWindow>
          );
        })()}
    </>
  );
};

export default RoutesLayerMultiLocationQuote;
